import '../css/ReportsTable.less';

import gql from 'graphql-tag';
import React from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import {Link} from 'react-router-dom';
import {Graph} from './HomePage/graphql';
import ImageIconHeader from './ImageIconHeader';
import WBReactTable from './WBReactTable';
import * as Urls from '../util/urls';
import LegacyWBIcon from './elements/LegacyWBIcon';
import {
  Maybe,
  useStarViewMutation,
  useUnstarViewMutation,
  useUpsertView2Mutation,
} from '../generated/graphql';
import {Popup} from 'semantic-ui-react';
import {isPublicRead} from '../util/projectAccess';
import makeComp from '../util/profiler';

const TimeAgo = require('react-timeago').default;

export interface ReportsTableReport {
  id: string;
  displayName: string;
  description: string;
  user: {
    id: string;
    username: string;
    photoUrl: string;
    admin: boolean;
  };
  project: {
    id: string;
    entityName: string;
    name: string;
    access: Maybe<string>;
  };
  updatedAt: string;
  starCount: number;
  starred: boolean;
  showcasedAt: Maybe<string>;
  previewUrl: Maybe<string>;
}

export const reportsTableFragment = gql`
  fragment ReportsTableFragment on View {
    id
    displayName
    description
    user {
      id
      username
      photoUrl
      admin
    }
    project {
      id
      name
      entityName
      access
    }
    starCount
    updatedAt
    starred
    showcasedAt
    previewUrl
  }
`;

interface ReportsTableProps {
  data: Graph<ReportsTableReport>;
  pageSize?: number | null;
  onChangePageSize?: (pageSize: number) => void;
  showShowcase?: boolean;
}
const ReportsTable: React.FC<ReportsTableProps> = makeComp(
  props => {
    const {data, pageSize, onChangePageSize, showShowcase} = props;
    const [starView] = useStarViewMutation();
    const [unstarView] = useUnstarViewMutation();
    const [upsertView] = useUpsertView2Mutation();

    const starReport = (id: string) => starView({variables: {id}});
    const unstarReport = (id: string) => unstarView({variables: {id}});
    const showcaseReport = (id: string) =>
      upsertView({variables: {id, showcasedAt: new Date()}});

    const toggleStarred = (r: ReportsTableReport) =>
      r.starred ? unstarReport(r.id) : starReport(r.id);

    const columns = [
      {Header: 'Title', accessor: 'title', width: 300},
      ...(showShowcase
        ? [
            {
              Header: 'Showcase',
              accessor: 'showcase',
              width: 100,
              className: 'showcase-cell',
              headerClassName: 'showcase-cell',
            },
          ]
        : []),
      {Header: '', accessor: 'stars', width: 60, className: 'stars-cell'},
      {Header: 'Project', accessor: 'project', width: 200},
      {Header: 'Author', accessor: 'author'},
      {Header: 'Last Edit', accessor: 'lastEdited'},
    ];
    const reports = _.map(data.edges, ({node: r}) => {
      const showcaseDisabled = !isPublicRead(r.project);
      const entityName = r.project.entityName;
      return {
        searchString: r.displayName,
        row: {
          title: (
            <Link
              to={Urls.reportView({
                entityName,
                projectName: r.project.name,
                reportID: r.id,
                reportName: r.displayName,
              })}>
              {r.displayName}
            </Link>
          ),
          ...(showShowcase
            ? {
                showcase:
                  r.showcasedAt == null ? (
                    <Popup
                      on="hover"
                      position="top center"
                      popperModifiers={{
                        preventOverflow: {enabled: false},
                      }}
                      content={
                        !showcaseDisabled
                          ? 'Showcase this report'
                          : 'Make this project public to showcase this report'
                      }
                      trigger={
                        <span>
                          <LegacyWBIcon
                            name="plus"
                            data-test="showcase-report-button"
                            className={classNames({
                              disabled: showcaseDisabled,
                            })}
                            onClick={() =>
                              !showcaseDisabled && showcaseReport(r.id)
                            }
                          />
                        </span>
                      }
                    />
                  ) : (
                    <Popup
                      on="hover"
                      position="top center"
                      popperModifiers={{
                        preventOverflow: {enabled: false},
                      }}
                      content="Featured at the top of your profile"
                      trigger={
                        <span>
                          <LegacyWBIcon
                            name="gallery"
                            data-test="go-to-showcase-button"
                            onClick={scrollToReportShowcase}
                          />
                        </span>
                      }
                    />
                  ),
              }
            : {}),
          stars: (
            <>
              <LegacyWBIcon
                onClick={() => toggleStarred(r)}
                name="heart"
                className={classNames({starred: r.starred})}
              />
              {r.starCount}
            </>
          ),
          project: (
            <Link to={`/${entityName}/${r.project.name}`}>
              {r.project.name}
            </Link>
          ),
          author: <Link to={`/${r.user.username}`}>{r.user.username}</Link>,
          lastEdited: <TimeAgo date={r.updatedAt + 'Z'} />,
        },
      };
    });
    return reports.length === 0 ? (
      <div />
    ) : (
      <>
        <ImageIconHeader icon="report" text="Reports" />
        <WBReactTable
          className="reports-table"
          pageSize={pageSize ?? undefined}
          onChangePageSize={onChangePageSize}
          data={reports}
          columns={columns}
        />
      </>
    );
  },
  {id: 'ReportsTable'}
);

export default ReportsTable;

function scrollToReportShowcase() {
  const el = document.querySelector('#report-showcase');
  if (el != null) {
    el.scrollIntoView({
      block: 'center',
      inline: 'center',
      behavior: 'smooth',
    });
  }
}
